import { Box, Button, Grid, MenuItem, TextField } from '@mui/material';
import { CODE_MASTER_SELECTION } from '../../../_constants/manageConst';
import { useTranslation } from 'react-i18next';
import {
  PFRCodeMasterSearchParam,
  RCodePartsMasterSearchParam,
} from '../../../stores/codeMaster/api';
import {
  getPCodeMaster,
  getFCodeMaster,
  getRCodeMaster,
  getRCodePartsMaster,
  resetRCodePartsList,
} from '../../../stores/codeMaster/slice';
import { AppDispatch, RootState } from '../../../stores/store';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { getMarketCodeList } from '../../../stores/marketCode/slice';
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DATE_FORMAT } from '../../../_constants/Const';
import { isFulfilled, isRejected } from '@reduxjs/toolkit';
import {
  isGetFCodeMaster,
  isGetMarketCodeList,
  isGetPCodeMaster,
  isGetRCodeMaster,
  isGetRCodePartsMaster,
} from '../../../_utils/typeGuardUtils';
import ErrorModal from '../../../_common/Modal/ErrorModal';
import useErrorModal from '../../../hooks/useErrorModal';
import { adjustInputYear, isEmptyData } from '../../../_utils/utils';
import { API_CODE } from '../../../_constants/apiConst';

type MasterManagementHeaderProps = {
  masterType: number;
  updateMasterType: (params: number) => void;
};

const requiredFieldsRCodeParts: (keyof RCodePartsMasterSearchParam)[] = [
  'code',
  'modelName',
  'market',
];

const defaultParamsPFRCode = {
  code: '',
  updateTimeFrom: '',
  updateTimeTo: '',
  name: '',
  eName: '',
  jName: '',
};

const defaultParamsRCodeParts = {
  code: '',
  modelName: '',
  market: -1,
};

const MasterManagementHeader: React.FC<MasterManagementHeaderProps> = ({
  masterType,
  updateMasterType,
}) => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const { error, showApiError, showTypeError, hideError } = useErrorModal();
  const { marketCodeList } = useSelector(
    (state: RootState) => state.marketCodeList,
  );
  const { userData } = useSelector((state: RootState) => state.userInfo);

  /** P・F・Rコードマスタの検索パラメーター */
  const [paramsPFRCode, setParamsPFRCode] =
    useState<PFRCodeMasterSearchParam>(defaultParamsPFRCode);

  const handleInputPFRCodeChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setParamsPFRCode({
      ...paramsPFRCode,
      [name]:
        typeof paramsPFRCode[name as keyof PFRCodeMasterSearchParam] ===
        'number'
          ? Number(value)
          : value,
    });
  };

  const handleInputDateChange = (
    value: string | number | Dayjs | null,
    name: keyof PFRCodeMasterSearchParam,
  ) => {
    let date = '';
    if (value instanceof Object) {
      // format通りの年月日の形式にする
      const formattedDate =
        value.year().toString() +
        '-' +
        Number(value.month() + 1).toString() +
        '-' +
        value.date().toString();
      const adjustedDate = adjustInputYear(dayjs(formattedDate));
      const formatString =
        userData.dateFormat === 2 // DD-MM-YY → DD-MM-YYYY
          ? DATE_FORMAT[1]
          : DATE_FORMAT[userData.dateFormat];
      date = adjustedDate ? dayjs(adjustedDate).format(formatString) : '';
    }
    setParamsPFRCode({
      ...paramsPFRCode,
      [name]: date,
    });
  };

  /** Rコード・パーツマスタの検索パラメーター */
  const [paramsRCodeParts, setParamsRCodeParts] =
    useState<RCodePartsMasterSearchParam>(defaultParamsRCodeParts);

  const handleInputRCodePartsChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setParamsRCodeParts({
      ...paramsRCodeParts,
      [name]:
        typeof paramsRCodeParts[name as keyof RCodePartsMasterSearchParam] ===
        'number'
          ? Number(value)
          : value,
    });
  };

  /** Rコード・パーツマスタにとって、検索条件すべて入力されたら活性化され、検索実施可能になる */
  const isDisabledSearchBtn = useMemo(() => {
    return !requiredFieldsRCodeParts.every((field) =>
      typeof paramsRCodeParts[field as keyof RCodePartsMasterSearchParam] ===
      'number'
        ? paramsRCodeParts[field] !== -1
        : paramsRCodeParts[field] !== '',
    );
  }, [paramsRCodeParts]);

  /** マスタのドロップダウン変更時 */
  useEffect(() => {
    (async () => {
      if (
        masterType === CODE_MASTER_SELECTION.R_CODE_REPAIR_PARTS.id &&
        marketCodeList.length === 0
      ) {
        const response = await dispatch(getMarketCodeList());
        if (isFulfilled(response)) {
          if (!isGetMarketCodeList(response.payload)) {
            showTypeError(API_CODE.MARKET_CODE_LIST);
            return;
          }
        } else {
          showApiError(response.payload, API_CODE.MARKET_CODE_LIST);
          return;
        }
      }
      /** 検索パラメーターをクリア */
      setParamsRCodeParts(defaultParamsRCodeParts);
      if (masterType !== CODE_MASTER_SELECTION.R_CODE_REPAIR_PARTS.id) {
        dispatch(resetRCodePartsList());
      }
      setParamsPFRCode(defaultParamsPFRCode);
      /** 初期表示は全データを表示（Rコード・パーツマスタ以外） */
      handleDisplay(false);
    })();
  }, [masterType]);

  /** 選択されたマスタの一覧を取得 */
  const handleDisplay = async (isClickSearchButton: boolean) => {
    const newParamsPFRCode = isClickSearchButton ? paramsPFRCode : {};
    switch (masterType) {
      case CODE_MASTER_SELECTION.P_CODE.id: {
        const response = await dispatch(getPCodeMaster(newParamsPFRCode));
        if (isRejected(response)) {
          showApiError(response.payload, API_CODE.P_CODE_MASTER);
          return;
        }
        if (!isGetPCodeMaster(response.payload)) {
          showTypeError(API_CODE.P_CODE_MASTER);
          return;
        }
        break;
      }
      case CODE_MASTER_SELECTION.F_CODE.id: {
        const response = await dispatch(getFCodeMaster(newParamsPFRCode));
        if (isRejected(response)) {
          showApiError(response.payload, API_CODE.F_CODE_MASTER);
          return;
        }
        if (!isGetFCodeMaster(response.payload)) {
          showTypeError(API_CODE.F_CODE_MASTER);
          return;
        }
        break;
      }
      case CODE_MASTER_SELECTION.R_CODE.id: {
        const response = await dispatch(getRCodeMaster(newParamsPFRCode));
        if (isRejected(response)) {
          showApiError(response.payload, API_CODE.R_CODE_MASTER);
          return;
        }
        if (!isGetRCodeMaster(response.payload)) {
          showTypeError(API_CODE.R_CODE_MASTER);
          return;
        }
        break;
      }
      case CODE_MASTER_SELECTION.R_CODE_REPAIR_PARTS.id: {
        // 初回表示はしない
        if (!isClickSearchButton) {
          return;
        }

        const response = await dispatch(getRCodePartsMaster(paramsRCodeParts));
        if (isRejected(response)) {
          showApiError(response.payload, API_CODE.R_CODE_PARTS_MASTER);
          return;
        }
        if (!isGetRCodePartsMaster(response.payload)) {
          showTypeError(API_CODE.R_CODE_PARTS_MASTER);
          return;
        }
        break;
      }
    }
  };

  /** クリアボタン押下 */
  const handleClear = () => {
    setParamsPFRCode(defaultParamsPFRCode);
    setParamsRCodeParts(defaultParamsRCodeParts);
  };

  return (
    <Box sx={{ py: 3, mx: '2%' }}>
      {/* コードマスタの選択 */}
      <Grid container spacing={1} alignItems="center">
        {/* コード */}
        <Grid size={3.25}>
          <TextField
            fullWidth
            label={t('LBL_MASTER')}
            name="master"
            size="small"
            value={masterType}
            select
            onChange={(e) => updateMasterType(Number(e.target.value))}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
          >
            <MenuItem key={`code_master_empty`} value={-1}>
              &nbsp;{/* 空の選択肢 */}
            </MenuItem>
            {Object.values(CODE_MASTER_SELECTION).map((value, i) => (
              <MenuItem key={`code_master_${i}`} value={value.id}>
                {t(value.key)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
      {/* コードマスタの検索 */}
      {/* P・F・Rコードマスタの検索 */}
      {[
        CODE_MASTER_SELECTION.P_CODE.id,
        CODE_MASTER_SELECTION.F_CODE.id,
        CODE_MASTER_SELECTION.R_CODE.id,
      ].some((item) => item === masterType) && (
        <Box>
          <Grid
            container
            spacing={2}
            sx={{ mt: 3 }}
            justifyContent="center"
            alignItems="center"
          >
            {/* 検索条件 */}
            <Grid size={3}>
              <TextField
                fullWidth
                label={t('LBL_CODE')}
                name="code"
                size="small"
                value={paramsPFRCode.code}
                onChange={handleInputPFRCodeChange}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
              />
            </Grid>
            <Grid size={3}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={t('LBL_UPDATE_TIME_FROM')}
                  value={
                    isEmptyData(paramsPFRCode.updateTimeFrom)
                      ? null
                      : dayjs(paramsPFRCode.updateTimeFrom)
                  }
                  onChange={(value) =>
                    handleInputDateChange(value, 'updateTimeFrom')
                  }
                  maxDate={dayjs(paramsPFRCode.updateTimeTo)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      InputLabelProps: { shrink: true },
                    },
                    field: {
                      clearable: true,
                      onClear: () =>
                        handleInputDateChange('', 'updateTimeFrom'),
                    },
                  }}
                  format={DATE_FORMAT[userData.dateFormat]}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={3}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={t('LBL_UPDATE_TIME_TO')}
                  value={
                    isEmptyData(paramsPFRCode.updateTimeTo)
                      ? null
                      : dayjs(paramsPFRCode.updateTimeTo)
                  }
                  onChange={(value) =>
                    handleInputDateChange(value, 'updateTimeTo')
                  }
                  minDate={dayjs(paramsPFRCode.updateTimeFrom)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      InputLabelProps: { shrink: true },
                    },
                    field: {
                      clearable: true,
                      onClear: () => handleInputDateChange('', 'updateTimeTo'),
                    },
                  }}
                  format={DATE_FORMAT[userData.dateFormat]}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={3}>
              <TextField
                fullWidth
                label={t('LBL_CODE_NAME')}
                name="name"
                size="small"
                value={paramsPFRCode.name}
                onChange={handleInputPFRCodeChange}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
              />
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            sx={{ mt: 3 }}
            justifyContent="center"
            alignItems="center"
          >
            <Grid size={3}>
              <TextField
                fullWidth
                label={t('LBL_E_CODE_NAME')}
                name="eName"
                size="small"
                value={paramsPFRCode.eName}
                onChange={handleInputPFRCodeChange}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                fullWidth
                label={t('LBL_J_CODE_NAME')}
                name="jName"
                size="small"
                value={paramsPFRCode.jName}
                onChange={handleInputPFRCodeChange}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
              />
            </Grid>
            {/* 検索ボタン */}
            <Grid size={1.5}>
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleDisplay(true)}
                  fullWidth
                >
                  {t('BTN_SEARCH')}
                </Button>
              </Box>
            </Grid>
            {/* クリアボタン */}
            <Grid size={1.5}>
              <Box display="flex" gap={2}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleClear}
                  fullWidth
                >
                  {t('BTN_CLEAR')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
      {/* Rコード・パーツマスタ */}
      {masterType === CODE_MASTER_SELECTION.R_CODE_REPAIR_PARTS.id && (
        <Grid
          container
          spacing={2}
          sx={{ mt: 3 }}
          justifyContent="center"
          alignItems="center"
        >
          {/* 検索条件 */}
          <Grid size={2}>
            <TextField
              fullWidth
              label={t('LBL_R_CODE')}
              name="code"
              size="small"
              value={paramsRCodeParts.code}
              onChange={handleInputRCodePartsChange}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          </Grid>
          <Grid size={2}>
            <TextField
              fullWidth
              label={t('LBL_MODEL')}
              name="modelName"
              size="small"
              value={paramsRCodeParts.modelName}
              onChange={handleInputRCodePartsChange}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          </Grid>
          <Grid size={2}>
            <TextField
              fullWidth
              label={t('LBL_MARKET')}
              name="market"
              select
              size="small"
              value={paramsRCodeParts.market}
              onChange={handleInputRCodePartsChange}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            >
              <MenuItem value={-1}>&nbsp;{/* 空の選択肢 */}</MenuItem>
              {marketCodeList.map((item, i) => (
                <MenuItem key={`market_code_${i}`} value={item.code}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {/* 検索ボタン */}
          <Grid size={2}>
            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleDisplay(true)}
                disabled={isDisabledSearchBtn}
                fullWidth
              >
                {t('BTN_SEARCH')}
              </Button>
            </Box>
          </Grid>
          {/* クリアボタン */}
          <Grid size={2}>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleClear}
                fullWidth
              >
                {t('BTN_CLEAR')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}
      {error && <ErrorModal message={error} onClose={hideError} />}
    </Box>
  );
};

export default MasterManagementHeader;
